import { updateOperations } from './../util/ngrx.util';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { AppState } from 'src/app/app.states';
import { FinancialTransactionActions } from 'src/app/features/financial-transactions/state/financial-transactions.action-types';
import { LoadAllOpportunities } from 'src/app/features/invoices/state/invoices.actions';
import { InvoicesActions } from 'src/app/features/invoices/state/invoices.action-types';
import { UsersSelectors } from 'src/app/features/users/state/users.selector-types';
import { filter, first, map, take } from 'rxjs/operators';
import { Roles } from '../enums/Roles.enum';
import { UsersActions } from 'src/app/features/users/state/users.action-types';
import { AuthActions } from 'src/app/features/authentication/state/auth.action-types';
import { UserStatus } from '../enums/UserStatus.enum';
import { FeatureFlagService } from './feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  baseEndpoint = environment.socketEndpoint;
  socket: Socket;
  loggedInUserRole;
  loggedInUserID;
  private readonly keynuaStateResponseThrottleMs = 800;
  private lastKeynuaStateResponseAt = 0;
  isConnected$: Subject<any> = new BehaviorSubject<any>(false);
  /** Multicast bus for realtime-driven UI (mirrors socket.io events → typed `type` payloads where used). */
  socketEvent = new Subject<any>();

  constructor(
    private store: Store<AppState>,
    private featureFlag: FeatureFlagService,
  ) {}

  isRealTimeEnabledForCurrentUserRole() {
    this.getCurrentUserId();

    return this.store
      .pipe(
        select(UsersSelectors.profileProperty('role')),
        map(currentRole =>
          [
            Roles.INVESTOR,
            Roles.CLIENT,
            Roles.CLIENT_HAPPINESS,
            Roles.RISK_ANALYST,
            Roles.ADMIN,
            Roles.SUPER_ADMIN,
            Roles.ACCOUNT_MANGER,
            Roles.ACCOUNT_MANAGER_SUPERVISOR,
            Roles.INVESTOR_SALES,
            Roles.INVESTOR_SALES_SUPERVISOR,
          ].includes(currentRole)
        ),
        first()
      )
      .toPromise();
  }

  async getCurrentUserId() {
    await this.store
      .select(UsersSelectors.profileProperty('_id'))
      .pipe(take(1))
      .subscribe(user => (this.loggedInUserID = user));
  }

  async disconnect() {
    if (await this.isRealTimeEnabledForCurrentUserRole()) {
      if (this.socket) {
        this.socket.off('keynuaState_response', this.onKeynuaStateResponse);
      }
      if (this.socket && !this.socket.disconnected) this.socket.disconnect();
    }
  }

  /**
   * FIN-1710 D112 · realtime when an **admin** approves the depósito · AC2.2.
   *
   * `'admin approve a transaction'` already emits `rt_transactions`, so no new event is
   * published — the profile reload is what moves the banner from *en proceso* to
   * *aprobado* without the investor touching anything.
   *
   * ⚠⚠ Both dispatches stay **inside** the identity checks. `emit` broadcasts to every
   * connected client, so outside them every investor on the platform would reload their
   * profile on any approval.
   *
   * A field rather than an inline closure so the branch is reachable from a spec, the way
   * `onKeynuaStateResponse` below already is.
   */
  private readonly onRtTransactions = (args: any) => {
    if (args?._id) {
      if (this.loggedInUserID === args._id) {
        this.store.dispatch(FinancialTransactionActions.LoadAll());
        this.store.dispatch(UsersActions.LoadProfile());
      }
      return;
    }

    if (args?.userIds) {
      // The bulk-approval cases publish a list instead of one id.
      if (args.userIds.some((u: any) => u === this.loggedInUserID)) {
        this.store.dispatch(FinancialTransactionActions.LoadAll());
        this.store.dispatch(InvoicesActions.LoadAll());
        this.store.dispatch(UsersActions.LoadProfile());
      }
    }
  };

  /**
   * FIN-1710 D93 · the transitional banner reads `laftData`, which only `getLaftData()`
   * refreshes — so reloading the profile alone left *Validando la firma* invisible until
   * something else happened to fetch the declaration. `onKeynuaStateResponse` below has
   * always done both halves; this is the missing one, copied from it.
   */
  private readonly onKeynuaWidgetCompleted = () => {
    console.log('keynua_widget_completed - reloading profile to show validating banner');
    this.store.dispatch(UsersActions.LoadProfile());
    this.socketEvent.next({ type: 'reloadLaftData' });
  };

  // FIN-1736 · a compliance verdict that changes no account status still has to reach
  // the banner, whose validation chip reads the declaration.
  private readonly onLaftValidationResponse = () => {
    this.store.dispatch(UsersActions.LoadProfile());
    this.socketEvent.next({ type: 'reloadLaftData' });
  };

  private readonly onKeynuaStateResponse = (_payload?: unknown) => {
    const now = Date.now();
    if (now - this.lastKeynuaStateResponseAt < this.keynuaStateResponseThrottleMs) return;
    this.lastKeynuaStateResponseAt = now;

    // Treat keynuaState_response as a pure refresh trigger. Backend payload may be null.
    this.store.dispatch(UsersActions.LoadProfile());
    this.socketEvent.next({ type: 'reloadLaftData' });
  };

  async setupSocketConnection(accessToken) {
    //console.log('!!!!REALTIME', await this.getCurrentUserId());

    if (await this.isRealTimeEnabledForCurrentUserRole()) {
      this.socket = io(this.baseEndpoint, {
        transports: ['websocket'],
        query: { accessToken },
      });

      /***********************/
      /* CORE EVENT LISTENERS */
      /***********************/
      this.socket.on('connect', () => {
        this.isConnected$.next(true);
      });

      this.socket.on('reconnect', () => {
        console.log('reconnect');
        //this.isConnected$.next(true);
      });

      this.socket.on('disconnect', () => {
        this.isConnected$.next(false);
        console.log('disconnect');
      });

      /*
      this.socket.onAny((event, ...args) => {
        console.log(`got ${event}`);
      });
      */
      /**********************/
      /* REAL TIME TRIGGERS */
      /**********************/

      this.socket.on('rt_opportunities', args => {
        //console.log(args)
        //this.store.dispatch(FinancialTransactionActions.LoadAll());
        this.store.dispatch(InvoicesActions.LoadAllOpportunities());
      });

      this.socket.on('laft_status', (payload: { laftStatus: string }) => {
        this.socketEvent.next({ type: 'laft_status', payload });
      });

      this.socket.on('contract_status', (payload: { contractStatus: string }) => {
        this.socketEvent.next({ type: 'contract_status', payload });
      });

      this.socket.on('financialTransactions_investments_new_success', payload => {
        this.store.dispatch(InvoicesActions.OpportunityUpdated(payload.recalculatedOpportunity));
      });

      this.socket.on('user_approved', args => {
        this.onUserStatusChanged(args);
      });

      this.socket.on('update_operation', args => {
        this.store.dispatch(InvoicesActions.UpdateOperations(args));
        this.socketEvent.next('update_operation');
      });

      this.socket.on('remove_operation', args => {
        this.store.dispatch(InvoicesActions.RemoveOperations(args));
      });

      this.socket.on('investments_update', payload => {
        this.store.dispatch(InvoicesActions.InvestmentsUpdated(payload.recalculatedOpportunity));
        this.socketEvent.next({
          name: 'investments_update',
          data: payload.recalculatedOpportunity,
        });
      });

      this.socket.on('update_client', args => {
        this.socketEvent.next('update_client');
      });

      this.socket.on('rt_transactions', this.onRtTransactions);

      this.socket.off('keynuaState_response', this.onKeynuaStateResponse);
      this.socket.on('keynuaState_response', this.onKeynuaStateResponse);

      // Keynua widget completed - user finished signature process in widget
      // Refresh the profile and the declaration behind the banner; no dialog is
      // closed and nothing navigates.
      this.socket.on('keynua_widget_completed', this.onKeynuaWidgetCompleted);

      this.socket.on('laft_validation_response', this.onLaftValidationResponse);

      /*****************/
      /* SOCKET EVENTS */
      /*****************/

      this.socket.on('connecting', () => {
        console.log('connecting');
      });
      this.socket.on('connection', () => {
        console.log('connection');
      });
      this.socket.on('connect_error', err => {
        console.log('connect_error');
        console.log(err);
      });
      this.socket.on('connect_timeout', () => {
        console.log('connect_timeout');
      });
      this.socket.on('reconnect_attempt', () => {
        console.log('reconnect_attempt');
      });
      this.socket.on('reconnect_error', () => {
        console.log('reconnect_error');
      });
      this.socket.on('reconnect_failed', () => {
        console.log('reconnect_failed');
      });
      this.socket.on('reconnecting', () => {
        console.log('reconnecting');
      });
      this.socket.on('ping', () => {
        console.log('ping');
      });
      this.socket.on('pong', () => {
        console.log('pong');
      });
    }
  }

  isSocketConnected() {
    return this.socket.connected;
  }

  sendMessage() {
    if (this.socket.disconnected) {
      this.socket.connect();
    }
    console.log(this.socket);
    this.socket.emit('my message', 'Hello there from Angular!!!!!');
  }
  // ⚠ `user_approved` is the name, not the meaning: the same event fires on a disapproval.
  private onUserStatusChanged(args: any): void {
    if (this.featureFlag.isEarlyDepositEnabled() && args?.status === UserStatus.Disapproved) {
      this.store.dispatch(AuthActions.LogOut());
      return;
    }

    this.store.dispatch(UsersActions.LoadProfile());
    this.store.dispatch(UsersActions.UpdateProfile(args));
    this.store.dispatch(InvoicesActions.LoadAll());
    // The banner's validation chip reads the declaration, not the profile.
    this.socketEvent.next({ type: 'reloadLaftData' });
  }
}
